import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  IconButton,
  Typography,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Box,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  Autocomplete
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { useAuth } from '../../../contexts/AuthContext';
import { checkBookService, CheckBook, CreateCheckBookRequest } from '../../../services/checkBookService';
import accountingService, { BankAccountDto } from '../../../services/accountingService';
import { useTranslation } from 'react-i18next';
import GuideButton from '../../common/guide/GuideButton';

// ✅ Genişletilmiş hesap interface'i - SubAccount için parentBankAccountId içerir
interface CheckAccountOption extends BankAccountDto {
  isSubAccount?: boolean;
  parentBankAccountId?: number;
  subAccountId?: number;
}

interface AddCheckBookModalProps {
  open: boolean;
  onClose: () => void;
  checkBookToEdit?: CheckBook | null;
}

const AddCheckBookModal: React.FC<AddCheckBookModalProps> = ({ open, onClose, checkBookToEdit }) => {
  const { userDetails } = useAuth();
  const { i18n } = useTranslation('accounting');
  const isTr = i18n.language?.toLowerCase().startsWith('tr');
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  const isEditMode = !!checkBookToEdit;

  // Form state
  const [serialNumber, setSerialNumber] = useState('');
  const [startNumber, setStartNumber] = useState<number | ''>('');
  const [endNumber, setEndNumber] = useState<number | ''>('');
  const [description, setDescription] = useState('');
  const [currency, setCurrency] = useState('TRY');
  const [isActive, setIsActive] = useState(true);
  const [selectedBankAccount, setSelectedBankAccount] = useState<CheckAccountOption | null>(null);

  // Data state
  const [checkAccounts, setCheckAccounts] = useState<CheckAccountOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Modal açıldığında verileri yükle
  useEffect(() => {
    if (open) {
      loadCheckAccounts();
      
      if (checkBookToEdit) {
        populateFormForEdit(checkBookToEdit);
      } else {
        resetForm();
      }
    }
  }, [open, checkBookToEdit]);

  // Çek hesaplarını yükle
  const loadCheckAccounts = async () => {
    if (!userDetails?.companyId) return;

    try {
      setInitialLoading(true);
      const accounts = await accountingService.getBankAccounts();
      
      // Hem ana hesapları hem de SubAccount'ları kontrol et
      const allCheckAccounts: CheckAccountOption[] = [];
      
      accounts.forEach((acc: BankAccountDto) => {
        const parentId = acc.id || acc.Id;
        const parentBankName = acc.bankName || acc.BankName;
        
        // Ana hesap çek hesabı mı?
        if ((acc.isCheckAccount || acc.IsCheckAccount) && !(acc.isDeleted || acc.IsDeleted)) {
          allCheckAccounts.push({
            ...acc,
            isSubAccount: false,
            parentBankAccountId: parentId,
          } as CheckAccountOption);
        }
        
        // SubAccount'ları kontrol et
        const subAccounts = acc.subAccounts || acc.SubAccounts || [];
        subAccounts.forEach((sub: any) => {
          if ((sub.isCheckAccount || sub.IsCheckAccount) && !(sub.isDeleted || sub.IsDeleted)) {
            const subId = sub.id || sub.Id;
            // SubAccount'u CheckAccountOption formatına dönüştür
            allCheckAccounts.push({
              id: subId,
              Id: subId,
              bankName: `${parentBankName} - ${sub.accountName || sub.AccountName}`,
              BankName: `${parentBankName} - ${sub.accountName || sub.AccountName}`,
              accountNumber: sub.accountNumber || sub.AccountNumber,
              AccountNumber: sub.accountNumber || sub.AccountNumber,
              currency: sub.currency || sub.Currency,
              Currency: sub.currency || sub.Currency,
              isCheckAccount: true,
              IsCheckAccount: true,
              isDeleted: false,
              IsDeleted: false,
              // ✅ SubAccount bilgileri
              isSubAccount: true,
              parentBankAccountId: parentId,
              subAccountId: subId,
            } as CheckAccountOption);
          }
        });
      });
      
      setCheckAccounts(allCheckAccounts);
      
      if (allCheckAccounts.length === 0) {
        setError(tt('Çek hesabı olarak tanımlanmış banka hesabı bulunamadı. Önce bir banka hesabını veya alt hesabını "Çek Hesabı" olarak işaretleyiniz.', 'No bank account marked as check account was found. Please mark a bank account or sub account as "Check Account" first.'));
      }
    } catch (err) {
      console.error('Çek hesapları yüklenirken hata:', err);
      setError(tt('Banka hesapları yüklenirken bir hata oluştu', 'An error occurred while loading bank accounts'));
    } finally {
      setInitialLoading(false);
    }
  };

  // Düzenleme modu için formu doldur
  const populateFormForEdit = (checkBook: CheckBook) => {
    setSerialNumber(checkBook.serialNumber);
    setStartNumber(checkBook.startNumber);
    setEndNumber(checkBook.endNumber);
    setDescription(checkBook.description || '');
    setCurrency(checkBook.currency);
    setIsActive(checkBook.isActive);
    
    // Banka hesabını bul ve seç
    if (checkBook.bankAccountId) {
      const account = checkAccounts.find(acc => 
        (acc.id || acc.Id) === checkBook.bankAccountId
      );
      setSelectedBankAccount(account || null);
    }
  };

  // Form sıfırla
  const resetForm = () => {
    setSerialNumber('');
    setStartNumber('');
    setEndNumber('');
    setDescription('');
    setCurrency('TRY');
    setIsActive(true);
    setSelectedBankAccount(null);
    setError(null);
    setSuccess(null);
  };

  // Yaprak sayısını hesapla
  const calculateLeaves = () => {
    if (startNumber !== '' && endNumber !== '' && endNumber >= startNumber) {
      return Number(endNumber) - Number(startNumber) + 1;
    }
    return 0;
  };

  // Form gönder
  const handleSubmit = async () => {
    if (!userDetails?.companyId) return;

    // Validasyon
    if (!serialNumber.trim()) {
      setError(tt('Seri numarası gereklidir', 'Serial number is required'));
      return;
    }
    if (!selectedBankAccount) {
      setError(tt('Banka hesabı seçmelisiniz', 'You must select a bank account'));
      return;
    }
    if (startNumber === '' || endNumber === '') {
      setError(tt('Başlangıç ve bitiş numaraları gereklidir', 'Start and end numbers are required'));
      return;
    }
    if (Number(startNumber) >= Number(endNumber)) {
      setError(tt('Başlangıç numarası bitiş numarasından küçük olmalıdır', 'Start number must be less than end number'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // ✅ SubAccount ise parentBankAccountId kullan, değilse kendi ID'sini kullan
      const isSubAccount = selectedBankAccount.isSubAccount === true;
      const bankAccountId = isSubAccount 
        ? selectedBankAccount.parentBankAccountId!
        : (selectedBankAccount.id || selectedBankAccount.Id)!;
      const subAccountId = isSubAccount 
        ? selectedBankAccount.subAccountId 
        : null;
      
      console.log('📘 Çek defteri kaydediliyor:', {
        isSubAccount,
        bankAccountId,
        subAccountId,
        selectedBankAccount: selectedBankAccount.bankName || selectedBankAccount.BankName
      });
      
      if (isEditMode && checkBookToEdit) {
        // Güncelleme
        await checkBookService.update(checkBookToEdit.id, userDetails.companyId, {
          serialNumber: serialNumber.trim(),
          description: description.trim() || undefined,
          isActive,
          startNumber: checkBookToEdit.usedLeaves === 0 ? Number(startNumber) : undefined,
          endNumber: checkBookToEdit.usedLeaves === 0 ? Number(endNumber) : undefined,
        });
        setSuccess(tt('Çek defteri güncellendi', 'Check book updated'));
      } else {
        // Yeni oluştur
        const data: CreateCheckBookRequest = {
          companyId: userDetails.companyId,
          bankAccountId: bankAccountId,
          subAccountId: subAccountId,  // ✅ SubAccountId eklendi
          serialNumber: serialNumber.trim(),
          startNumber: Number(startNumber),
          endNumber: Number(endNumber),
          currency,
          description: description.trim() || undefined,
        };
        console.log('📘 API\'ye gönderilen data:', data);
        await checkBookService.create(data);
        setSuccess(tt('Çek defteri oluşturuldu', 'Check book created'));
      }

      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (err: any) {
      console.error('❌ Çek defteri kaydetme hatası:', err);
      setError(err.message || tt('İşlem sırasında bir hata oluştu', 'An error occurred during the operation'));
    } finally {
      setLoading(false);
    }
  };

  // Modal kapat
  const handleClose = () => {
    if (!loading) {
      resetForm();
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <MenuBookIcon color="primary" />
            <Typography variant="h6">
              {isEditMode ? tt('Çek Defteri Düzenle', 'Edit Check Book') : tt('Yeni Çek Defteri', 'New Check Book')}
            </Typography>
            <GuideButton guideKey="finance.addCheckBook" size="small" />
          </Box>
          <IconButton onClick={handleClose} disabled={loading}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {initialLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {/* Hata/Başarı mesajları */}
            {error && (
              <Grid item xs={12}>
                <Alert severity="error" onClose={() => setError(null)}>
                  {error}
                </Alert>
              </Grid>
            )}
            {success && (
              <Grid item xs={12}>
                <Alert severity="success">
                  {success}
                </Alert>
              </Grid>
            )}

            {/* Banka Hesabı Seçimi */}
            <Grid item xs={12}>
              <Autocomplete
                options={checkAccounts}
                getOptionLabel={(option) => 
                  `${option.bankName || option.BankName} - ${option.accountNumber || option.AccountNumber}`
                }
                value={selectedBankAccount}
                onChange={(_, value) => {
                  setSelectedBankAccount(value);
                  if (value) {
                    setCurrency(value.currency || value.Currency || 'TRY');
                  }
                }}
                disabled={isEditMode || loading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={tt('Banka Hesabı *', 'Bank Account *')}
                    placeholder={tt('Çek hesabı seçin', 'Select check account')}
                    helperText={checkAccounts.length === 0 ? 
                      tt('Çek hesabı bulunamadı. Önce banka hesabı tanımlayın.', 'No check account found. Define a bank account first.') : 
                      tt('Sadece "Çek Hesabı" olarak işaretlenmiş hesaplar listelenir', 'Only accounts marked as "Check Account" are listed')
                    }
                  />
                )}
                noOptionsText={tt('Çek hesabı bulunamadı', 'No check account found')}
              />
            </Grid>

            {/* Seri Numarası */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={tt('Seri Numarası *', 'Serial Number *')}
                value={serialNumber}
                onChange={(e) => setSerialNumber(e.target.value.toUpperCase())}
                disabled={loading}
                placeholder={tt('Örn: ABC123', 'E.g.: ABC123')}
                helperText={tt('Çek defterinin seri numarası', 'Serial number of the check book')}
              />
            </Grid>

            {/* Para Birimi */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth disabled={isEditMode || loading}>
                <InputLabel>{tt('Para Birimi', 'Currency')}</InputLabel>
                <Select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  label={tt('Para Birimi', 'Currency')}
                >
                  <MenuItem value="TRY">TRY - {tt('Türk Lirası', 'Turkish Lira')}</MenuItem>
                  <MenuItem value="USD">USD - {tt('Amerikan Doları', 'US Dollar')}</MenuItem>
                  <MenuItem value="EUR">EUR - Euro</MenuItem>
                  <MenuItem value="GBP">GBP - {tt('İngiliz Sterlini', 'British Pound')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Başlangıç Numarası */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={tt('Başlangıç Numarası *', 'Start Number *')}
                type="number"
                value={startNumber}
                onChange={(e) => setStartNumber(e.target.value ? Number(e.target.value) : '')}
                disabled={loading || (isEditMode && (checkBookToEdit?.usedLeaves ?? 0) > 0)}
                placeholder={tt('Örn: 1', 'E.g.: 1')}
                helperText={isEditMode && (checkBookToEdit?.usedLeaves ?? 0) > 0 ? 
                  tt('Kullanılmış defterde değiştirilemez', 'Cannot be changed for used checkbooks') : 
                  tt('İlk çek numarası', 'First check number')
                }
                inputProps={{ min: 1 }}
              />
            </Grid>

            {/* Bitiş Numarası */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={tt('Bitiş Numarası *', 'End Number *')}
                type="number"
                value={endNumber}
                onChange={(e) => setEndNumber(e.target.value ? Number(e.target.value) : '')}
                disabled={loading || (isEditMode && (checkBookToEdit?.usedLeaves ?? 0) > 0)}
                placeholder={tt('Örn: 50', 'E.g.: 50')}
                helperText={isEditMode && (checkBookToEdit?.usedLeaves ?? 0) > 0 ? 
                  tt('Kullanılmış defterde değiştirilemez', 'Cannot be changed for used checkbooks') : 
                  tt('Son çek numarası', 'Last check number')
                }
                inputProps={{ min: 1 }}
              />
            </Grid>

            {/* Yaprak Sayısı Bilgisi */}
            {calculateLeaves() > 0 && (
              <Grid item xs={12}>
                <Alert severity="info" icon={<MenuBookIcon />}>
                  {tt('Bu çek defterinde', 'This check book will contain')} <strong>{calculateLeaves()}</strong> {tt('yaprak bulunacak.', 'leaves.')}
                  <br />
                  {tt('Numara aralığı', 'Number range')}: <strong>{startNumber}</strong> - <strong>{endNumber}</strong>
                </Alert>
              </Grid>
            )}

            {/* Açıklama */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={tt('Açıklama', 'Description')}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={loading}
                multiline
                rows={2}
                placeholder={tt('Opsiyonel açıklama...', 'Optional description...')}
              />
            </Grid>

            {/* Aktif Switch (sadece düzenleme modunda) */}
            {isEditMode && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                      disabled={loading}
                    />
                  }
                  label={tt('Aktif', 'Active')}
                />
              </Grid>
            )}
          </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          {tt('İptal', 'Cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || initialLoading || checkAccounts.length === 0}
          startIcon={loading ? <CircularProgress size={20} /> : undefined}
        >
          {isEditMode ? tt('Güncelle', 'Update') : tt('Kaydet', 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddCheckBookModal;
